import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { supplierService } from '../../services/supplierService';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';
import TableWithMonthGroups from '../../components/Common/TableWithMonthGroups';
import { formatDateLocaleLima } from '../../utils/dateUtils';
import MutateGate from '../../components/Common/MutateGate';
import { useCanMutate } from '../../hooks/useCanMutate';
import { isPurchaseOrderReadOnly, canReopenPurchaseOrder } from '../../utils/purchaseOrderProgress';
import PurchaseOrderPdfButton from '../../components/PurchaseOrders/PurchaseOrderPdfButton';
import { formatLinkedRequirementCodes } from '../../utils/purchaseOrderRequirements';
import BuyerUserCell from '../../components/Common/BuyerUserCell';
import ComprasProjectFilterSelect from '../../components/Common/ComprasProjectFilterSelect';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { formatLineProjectsLabel } from '../../utils/lineProjectLabels';

const PurchaseOrders = () => {
  const canMutate = useCanMutate();
  const { productsProjectEnabled } = useModuleSettings();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [orderToToggle, setOrderToToggle] = useState(null);
  const [isCloseFulfillmentModalOpen, setIsCloseFulfillmentModalOpen] = useState(false);
  const [orderToCloseFulfillment, setOrderToCloseFulfillment] = useState(null);
  const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
  const [orderToReopen, setOrderToReopen] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoadedInitialData = useRef(false);
  const previousFilters = useRef({ statusFilter: 'all', selectedSupplier: '', searchTerm: '', dateFrom: '', dateTo: '', projectFilter: 'all' });
  const isFirstRender = useRef(true);

  useEffect(() => {
    console.log('🔄 [PurchaseOrders/Index] useEffect inicial ejecutado');
    console.log('🔍 [PurchaseOrders/Index] hasLoadedInitialData.current:', hasLoadedInitialData.current);
    
    if (!hasLoadedInitialData.current) {
      console.log('✅ [PurchaseOrders/Index] Primera carga - ejecutando loadSuppliers() y loadOrders()');
      console.log('📍 [PurchaseOrders/Index] Razón: Necesitamos proveedores para el filtro de búsqueda en la página');
      hasLoadedInitialData.current = true;
      // Guardar los valores iniciales de los filtros
      previousFilters.current = { statusFilter, selectedSupplier, searchTerm, dateFrom, dateTo, projectFilter };
      loadSuppliers();
      loadOrders();
    } else {
      console.log('⏭️ [PurchaseOrders/Index] Ya se cargaron los datos iniciales anteriormente, omitiendo carga');
    }
  }, []);

  useEffect(() => {
    if (!productsProjectEnabled) return;
    purchaseOrderService.getFormData().then((res) => {
      if (res.success) setProjects(res.data?.projects || []);
    }).catch(() => {});
  }, [productsProjectEnabled]);

  useEffect(() => {
    // Saltar la primera ejecución (cuando se monta el componente)
    if (isFirstRender.current) {
      console.log('⏭️ [PurchaseOrders/Index] Primera ejecución del useEffect de filtros, omitiendo (es el montaje inicial)');
      isFirstRender.current = false;
      // Actualizar los filtros previos para la próxima vez
      previousFilters.current = { statusFilter, selectedSupplier, searchTerm, dateFrom, dateTo, projectFilter };
      return;
    }

    // Solo recargar órdenes si los filtros realmente cambiaron
    if (hasLoadedInitialData.current) {
      const filtersChanged = 
        previousFilters.current.statusFilter !== statusFilter ||
        previousFilters.current.selectedSupplier !== selectedSupplier ||
        previousFilters.current.searchTerm !== searchTerm ||
        previousFilters.current.dateFrom !== dateFrom ||
        previousFilters.current.dateTo !== dateTo ||
        previousFilters.current.projectFilter !== projectFilter;
      
      if (filtersChanged) {
        console.log('🔄 [PurchaseOrders/Index] Filtros cambiados, recargando órdenes');
        console.log('📋 [PurchaseOrders/Index] Filtros anteriores:', previousFilters.current);
        console.log('📋 [PurchaseOrders/Index] Filtros nuevos:', { statusFilter, selectedSupplier, searchTerm, dateFrom, dateTo, projectFilter });
        previousFilters.current = { statusFilter, selectedSupplier, searchTerm, dateFrom, dateTo, projectFilter };
        loadOrders();
      } else {
        console.log('⏭️ [PurchaseOrders/Index] Filtros no han cambiado, omitiendo recarga');
      }
    } else {
      console.log('⏭️ [PurchaseOrders/Index] Aún no se han cargado datos iniciales, omitiendo recarga');
    }
  }, [statusFilter, selectedSupplier, searchTerm, dateFrom, dateTo, projectFilter]);

  const loadSuppliers = async () => {
    console.log('🔍 [PurchaseOrders/Index] loadSuppliers() llamado');
    console.log('📍 [PurchaseOrders/Index] Razón: Cargar lista de proveedores para el filtro de búsqueda');
    console.log('⏰ [PurchaseOrders/Index] Timestamp:', new Date().toISOString());
    
    try {
      console.log('📡 [PurchaseOrders/Index] Llamando a supplierService.getSuppliers() con params:', { active_only: true });
      const response = await supplierService.getSuppliers({ active_only: true });
      
      console.log('✅ [PurchaseOrders/Index] Respuesta recibida de supplierService');
      console.log('📦 [PurchaseOrders/Index] Response:', response);
      
      if (response.success) {
        console.log('✅ [PurchaseOrders/Index] response.success = true');
        console.log('📊 [PurchaseOrders/Index] Cantidad de proveedores recibidos:', response.data?.length || 0);
        setSuppliers(response.data);
        console.log('✅ [PurchaseOrders/Index] Estado suppliers actualizado con', response.data?.length || 0, 'proveedores');
      } else {
        console.warn('⚠️ [PurchaseOrders/Index] response.success = false');
        console.warn('📋 [PurchaseOrders/Index] Response completa:', response);
      }
    } catch (error) {
      console.error('❌ [PurchaseOrders/Index] Error al cargar proveedores:', error);
      console.error('📊 [PurchaseOrders/Index] Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
    }
  };

  const loadOrders = async () => {
    console.log('🔍 [PurchaseOrders/Index] loadOrders() llamado');
    console.log('⏰ [PurchaseOrders/Index] Timestamp:', new Date().toISOString());
    
    setLoadingOrders(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (selectedSupplier) params.supplier_id = selectedSupplier;
      if (searchTerm) params.search = searchTerm;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (productsProjectEnabled && projectFilter !== 'all') params.project_id = projectFilter;
      
      console.log('📋 [PurchaseOrders/Index] Parámetros de búsqueda:', params);
      console.log('🔍 [PurchaseOrders/Index] Filtros aplicados:', {
        statusFilter,
        selectedSupplier,
        searchTerm,
        dateFrom,
        dateTo
      });
      console.log('📡 [PurchaseOrders/Index] Llamando a purchaseOrderService.getPurchaseOrders()');
      console.log('🔗 [PurchaseOrders/Index] Endpoint: GET /api/purchase-orders');
      
      const response = await purchaseOrderService.getPurchaseOrders(params);
      
      console.log('✅ [PurchaseOrders/Index] Respuesta recibida de purchaseOrderService');
      console.log('📦 [PurchaseOrders/Index] Response completa:', response);
      console.log('🔢 [PurchaseOrders/Index] response.success:', response.success);
      
      if (response.success && response.data) {
        console.log('✅ [PurchaseOrders/Index] response.success = true y response.data existe');
        console.log('📊 [PurchaseOrders/Index] Cantidad de órdenes recibidas:', response.data?.length || 0);
        console.log('📋 [PurchaseOrders/Index] Datos de órdenes:', response.data);
        setOrders(response.data);
        console.log('✅ [PurchaseOrders/Index] Estado orders actualizado con', response.data?.length || 0, 'órdenes');
      } else {
        console.warn('⚠️ [PurchaseOrders/Index] response.success = false o response.data no existe');
        console.warn('📋 [PurchaseOrders/Index] Response completa:', response);
      }
    } catch (error) {
      console.error('❌ [PurchaseOrders/Index] Error al cargar órdenes:', error);
      console.error('📊 [PurchaseOrders/Index] Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        stack: error.stack
      });
      setToast({
        isOpen: true,
        message: 'Error al cargar las órdenes de compra',
        type: 'error'
      });
    } finally {
      setLoadingOrders(false);
      console.log('🏁 [PurchaseOrders/Index] loadOrders() finalizado, loadingOrders = false');
    }
  };

  const handleToggleStatusClick = (order) => {
    setOrderToToggle(order);
    setIsToggleModalOpen(true);
  };

  const handleCloseFulfillmentClick = (order) => {
    setOrderToCloseFulfillment(order);
    setIsCloseFulfillmentModalOpen(true);
  };

  const handleReopenClick = (order) => {
    setOrderToReopen(order);
    setIsReopenModalOpen(true);
  };

  const handleReopenConfirm = async () => {
    if (!orderToReopen) return;
    setLoading(true);
    try {
      const response = await purchaseOrderService.reopenFulfillment(orderToReopen.id);
      await loadOrders();
      setIsReopenModalOpen(false);
      setOrderToReopen(null);
      setToast({
        isOpen: true,
        message: response.message || 'Orden reabierta correctamente',
        type: 'success',
      });
    } catch (error) {
      setToast({
        isOpen: true,
        message:
          error.response?.data?.message || 'No se pudo reabrir la orden de compra',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseFulfillmentConfirm = async () => {
    if (!orderToCloseFulfillment) return;
    setLoading(true);
    try {
      const response = await purchaseOrderService.closeFulfillment(orderToCloseFulfillment.id, {
        notes: 'Cerrada desde listado: no habrá más documentos por el saldo pendiente de la orden.',
      });
      await loadOrders();
      setIsCloseFulfillmentModalOpen(false);
      setOrderToCloseFulfillment(null);
      setToast({
        isOpen: true,
        message: response.message || 'Orden marcada como completada',
        type: 'success',
      });
    } catch (error) {
      setToast({
        isOpen: true,
        message:
          error.response?.data?.message || 'No se pudo cerrar la orden de compra',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatusConfirm = async () => {
    if (!orderToToggle) return;
    
    setLoading(true);
    try {
      const response = await purchaseOrderService.togglePurchaseOrderStatus(orderToToggle.id);
      await loadOrders();
      setIsToggleModalOpen(false);
      setOrderToToggle(null);
      setToast({
        isOpen: true,
        message: response.message || (orderToToggle.status === 'cancelled' 
          ? 'Orden de compra reactivada exitosamente' 
          : 'Orden de compra cancelada exitosamente'),
        type: 'success'
      });
    } catch (error) {
      console.error('Error al cambiar estado de orden:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || error.message || 'Error al cambiar el estado de la orden de compra',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const creatorOptions = useMemo(() => {
    const map = new Map();
    orders.forEach((row) => {
      const id = row.creator?.id ?? row.created_by;
      const name = row.creator?.name;
      if (id && name && !map.has(String(id))) map.set(String(id), name);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (userFilter === 'all') return orders;
    return orders.filter((row) => String(row.creator?.id ?? row.created_by ?? '') === String(userFilter));
  }, [orders, userFilter]);

  // Calcular estadísticas
  const totalOrders = filteredOrders.length;
  const issuedOrders = filteredOrders.filter(o => o.status === 'issued').length;
  const cancelledOrders = filteredOrders.filter(o => o.status === 'cancelled').length;
  const totalAmount = filteredOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);

  // Calcular progreso de items basado en cantidad usada en documentos
  const getProgressPercentage = (order) => {
    if (!order.items || order.items.length === 0) return 0;
    
    const totalQuantity = order.items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
    
    // Calcular cantidad usada: quantity - pending_quantity
    const usedQuantity = order.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity || 0);
      const pendingQty = item.pending_quantity !== undefined ? parseFloat(item.pending_quantity || 0) : qty;
      const usedQty = qty - pendingQty;
      return sum + usedQty;
    }, 0);
    
    return totalQuantity > 0 ? Math.round((usedQuantity / totalQuantity) * 100) : 0;
  };

  // Verificar si la orden tiene progreso (ya se ha usado en documentos)
  const hasProgress = (order) => {
    if (!order.items || order.items.length === 0) return false;
    
    // Verificar si algún item tiene pending_quantity menor que quantity
    return order.items.some(item => {
      const qty = parseFloat(item.quantity || 0);
      const pendingQty = item.pending_quantity !== undefined ? parseFloat(item.pending_quantity || 0) : qty;
      return pendingQty < qty;
    });
  };

  const handlePdfSuccess = (order) => {
    setToast({
      isOpen: true,
      message: `PDF de ${order.code} descargado`,
      type: 'success',
    });
  };

  const handlePdfError = (message) => {
    setToast({
      isOpen: true,
      message: message || 'Error al generar el PDF',
      type: 'error',
    });
  };

  const renderPdfAction = (order) => (
    <PurchaseOrderPdfButton
      orderId={order.id}
      onSuccess={handlePdfSuccess}
      onError={handlePdfError}
    />
  );

  return (
    <div>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Órdenes de Compra</h1>
                <p className="text-gray-600 mt-1">Gestión de órdenes de compra a proveedores</p>
              </div>
              <MutateGate>
                <Link
                  to="/purchase-orders/create"
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors inline-block"
                >
                  + Nueva Orden de Compra
                </Link>
              </MutateGate>
            </div>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <Link to="/compras" className="hover:text-teal-600">Compras</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Órdenes de Compra</span>
            </nav>

            {/* Filtros, Búsqueda y Rango de Fechas */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
                {/* Filtro por Estado */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los Estados</option>
                  <option value="issued">Emitidas</option>
                  <option value="cancelled">Canceladas</option>
                </select>

                {/* Filtro por Usuario */}
                <select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los Usuarios</option>
                  {creatorOptions.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>

                {productsProjectEnabled && (
                  <ComprasProjectFilterSelect
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                    projects={projects}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                )}

                {/* Búsqueda */}
                <input
                  type="text"
                  placeholder="Buscar por código o proveedor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />

                {/* Fecha Desde */}
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Fecha desde"
                />

                {/* Fecha Hasta */}
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Fecha hasta"
                />
              </div>
            </div>

            {/* Estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Total</div>
                <div className="text-2xl font-bold text-gray-800">{totalOrders}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Emitidas</div>
                <div className="text-2xl font-bold text-teal-600">{issuedOrders}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Canceladas</div>
                <div className="text-2xl font-bold text-gray-500">{cancelledOrders}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Monto Total</div>
                <div className="text-2xl font-bold text-blue-600">S/ {parseFloat(totalAmount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            </div>
          </div>

          {/* Tabla de Órdenes */}
          {loadingOrders ? (
            <div className="text-center py-12">
              <div className="text-gray-500">Cargando órdenes de compra...</div>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">No se encontraron órdenes de compra</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <TableWithMonthGroups
                data={filteredOrders}
                getDateForGrouping={(o) => o.order_date}
                defaultCollapsed
                columnCount={productsProjectEnabled ? 10 : 9}
                wrapperClassName="w-full"
                className="!w-full table-fixed"
                colgroup={(
                  <colgroup>
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '27%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '11%' }} />
                  </colgroup>
                )}
                emptyMessage="No se encontraron órdenes de compra"
                headerRow={
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entidad</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requerimiento</th>
                    {productsProjectEnabled && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proyecto</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Orden</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progreso</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                }
                renderRow={(order) => {
                  const progress = order.fulfillment_percent ?? getProgressPercentage(order);
                  const isFulfillmentClosed =
                    Boolean(order.fulfillment_closed_at) || order.is_fulfillment_closed;
                  const canReopen = canReopenPurchaseOrder(order);
                  const readOnlyOrder = !canReopen && isPurchaseOrderReadOnly(order);
                  const orderHasProgress =
                    !canReopen && !readOnlyOrder && (hasProgress(order) || progress > 0);
                  const statusLabel = order.status === 'cancelled'
                    ? 'Cancelada'
                    : isFulfillmentClosed
                    ? 'Cerrada'
                    : progress >= 100
                    ? 'Completada'
                    : progress > 0
                    ? 'Parcial'
                    : 'Emitida';
                  const statusClass = order.status === 'cancelled'
                    ? 'bg-red-100 text-red-800'
                    : isFulfillmentClosed
                    ? 'bg-slate-100 text-slate-800'
                    : progress >= 100
                    ? 'bg-blue-100 text-blue-800'
                    : progress > 0
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-green-100 text-green-800';
                  return (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        {canReopen ? (
                          <span className="text-sm font-mono text-gray-600">{order.code}</span>
                        ) : readOnlyOrder ? (
                          <Link
                            to={`/purchase-orders/${order.id}/edit?modo=ver`}
                            className="text-sm font-mono text-teal-600 hover:text-teal-900"
                          >
                            {order.code}
                          </Link>
                        ) : orderHasProgress ? (
                          <span className="text-sm font-mono text-gray-600">{order.code}</span>
                        ) : (
                          <Link
                            to={`/purchase-orders/${order.id}/edit`}
                            className="text-sm font-mono text-teal-600 hover:text-teal-900"
                          >
                            {order.code}
                          </Link>
                        )}
                        <BuyerUserCell creator={order.creator} />
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900 break-words leading-snug">{order.supplier?.name || 'N/A'}</div>
                        {order.supplier?.ruc && <div className="text-xs text-gray-500">RUC: {order.supplier.ruc}</div>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-teal-700">
                        {formatLinkedRequirementCodes(order) || <span className="text-gray-400">—</span>}
                      </td>
                      {productsProjectEnabled && (
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {formatLineProjectsLabel(order.items)}
                        </td>
                      )}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateLocaleLima(order.order_date)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{order.items?.length || 0} items</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div className="bg-teal-600 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
                          </div>
                          <span className="text-xs text-gray-600">{progress}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        S/ {parseFloat(order.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm font-medium">
                        {canReopen ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <MutateGate>
                              <button
                                type="button"
                                onClick={() => handleReopenClick(order)}
                                className="text-teal-600 hover:text-teal-900 font-medium"
                                title="Reabrir la orden y restaurar su estado de avance anterior"
                              >
                                Abrir
                              </button>
                            </MutateGate>
                            {renderPdfAction(order)}
                          </div>
                        ) : readOnlyOrder ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <Link
                              to={`/purchase-orders/${order.id}/edit?modo=ver`}
                              className="text-teal-600 hover:text-teal-900"
                              title="Ver orden (solo lectura)"
                            >
                              Ver
                            </Link>
                            {renderPdfAction(order)}
                          </div>
                        ) : orderHasProgress ? (
                          <div className="flex flex-wrap items-center gap-2">
                            {renderPdfAction(order)}
                            <MutateGate>
                              <button
                                type="button"
                                onClick={() => handleCloseFulfillmentClick(order)}
                                className="text-amber-700 hover:text-amber-900 whitespace-nowrap"
                                title="No habrá más documentos: dar la orden por completada"
                              >
                                Cerrar como completada
                              </button>
                            </MutateGate>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <Link
                              to={`/purchase-orders/${order.id}/edit`}
                              className="text-purple-600 hover:text-purple-900"
                              title={canMutate ? 'Editar' : 'Ver detalle'}
                            >
                              {canMutate ? 'Editar' : 'Ver'}
                            </Link>
                            {renderPdfAction(order)}
                            <MutateGate>
                              <button type="button" onClick={() => handleToggleStatusClick(order)} className={order.status === 'issued' ? "text-orange-600 hover:text-orange-900" : "text-green-600 hover:text-green-900"} title={order.status === 'issued' ? 'Cancelar' : 'Reactivar'}>
                                {order.status === 'issued' ? 'Cancelar' : 'Reactivar'}
                              </button>
                            </MutateGate>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                }}
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={isReopenModalOpen}
        onClose={() => {
          setIsReopenModalOpen(false);
          setOrderToReopen(null);
        }}
        onConfirm={handleReopenConfirm}
        title="Reabrir orden de compra"
        message={
          orderToReopen
            ? `¿Reabrir la orden "${orderToReopen.code}"? Volverá a su estado anterior según documentos y guías registradas (Emitida o Parcial) y podrá registrar más comprobantes o cerrarla de nuevo.`
            : ''
        }
        confirmText="Sí, reabrir"
        cancelText="No"
      />

      <ConfirmModal
        isOpen={isCloseFulfillmentModalOpen}
        onClose={() => {
          setIsCloseFulfillmentModalOpen(false);
          setOrderToCloseFulfillment(null);
        }}
        onConfirm={handleCloseFulfillmentConfirm}
        title="Cerrar orden de compra"
        message={
          orderToCloseFulfillment
            ? `¿Marcar "${orderToCloseFulfillment.code}" como completada? Use esta opción si ya no registrará más documentos por el saldo pendiente (la orden dejó de estar "en uso" para nuevos comprobantes).`
            : ''
        }
        confirmText="Sí, cerrar orden"
        cancelText="No"
      />

      {/* Modal de confirmación de cambio de estado */}
      <ConfirmModal
        isOpen={isToggleModalOpen}
        onClose={() => {
          setIsToggleModalOpen(false);
          setOrderToToggle(null);
        }}
        onConfirm={handleToggleStatusConfirm}
        title={orderToToggle?.status === 'issued' ? 'Cancelar Orden de Compra' : 'Reactivar Orden de Compra'}
        message={orderToToggle?.status === 'issued' 
          ? `¿Estás seguro de que deseas cancelar la orden "${orderToToggle?.code}"?`
          : `¿Estás seguro de que deseas reactivar la orden "${orderToToggle?.code}"?`}
        confirmText={orderToToggle?.status === 'issued' ? 'Cancelar Orden' : 'Reactivar Orden'}
        cancelText="No"
        isLoading={loading}
      />

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default PurchaseOrders;


